import React from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

import { DATE_FORMAT } from '../../config'
import CustomTag from './CustomTag'
import PropTypes from 'prop-types'
import { translate } from '../../cls/TranslationContext'
// Convert milliseconds to seconds if necessary
/**
 * Normalize a timestamp to seconds if provided in milliseconds.
 *
 * @param {number|string} timestamp – The timestamp in seconds or milliseconds.
 * @returns {number} The timestamp in seconds.
 */
const seccondsFromTimestamp = (timestamp) => {
	// Convert milliseconds to seconds if necessary
	return String(timestamp).length === 13
		? Math.floor(timestamp / 1000)
		: timestamp
}

// Calculate the number of days until the timestamp
/**
 * Calculate days difference between now and a given timestamp.
 *
 * @param {number|string} timestamp – The target timestamp (seconds or milliseconds).
 * @returns {number} Number of days from now until the timestamp.
 */
export const daysUntilTimestamp = (timestamp) => {
	const correctedTimestamp = seccondsFromTimestamp(timestamp)
	const now = dayjs()
	const then = dayjs.unix(correctedTimestamp)
	const days = then.diff(now, 'days')
	return days
}

/**
 * DueIn component displays how many days remain until (or past) a timestamp.
 * If `valueOnly` is true, it returns the raw days number.
 * Otherwise, it wraps the value in a CustomTag with color and tooltip.
 *
 * @component
 * @param {Object} props
 * @param {number|string} props.timestamp – The due timestamp.
 * @param {boolean} [props.valueOnly=false] – If true, render only the numeric value.
 * @returns {JSX.Element|number}
 */
export const DueIn = ({ timestamp, valueOnly = false }) => {
	const days = daysUntilTimestamp(timestamp)
	if (valueOnly) return days

	if (days === 0)
		return (
			<CustomTag color="warning" tooltip={translate('single.today')}>
				{translate('single.today')}
			</CustomTag>
		)
	if (days < 0)
		return (
			<CustomTag color="error" tooltip={translate('single.overdue')}>
				{days}
				{translate('single.day')}
			</CustomTag>
		)

	return (
		<CustomTag
			color="default"
			tooltip={translate('_day.remaining')}
			className="we-formated-timestamp"
		>
			{days}
			{translate('single.day')}
		</CustomTag>
	)
}
DueIn.propTypes = {
	timestamp: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	valueOnly: PropTypes.bool
}

/**
 * FormatedTimestamp component formats a Unix timestamp into a date string.
 * If no timestamp is provided, it returns null.
 *
 * @component
 * @param {Object} props
 * @param {number|string} props.timestamp – The timestamp to format.
 * @param {string} [props.formatedAs=DATE_FORMAT] – The date format string.
 * @returns {JSX.Element|null} Formatted date or null.
 */
export const FormatedTimestamp = ({
	timestamp,
	utcTime,
	formatedAs = DATE_FORMAT
}) => {
	if (!formatedAs) formatedAs = DATE_FORMAT
	if (utcTime) {
		dayjs.extend(utc)
		timestamp = dayjs.utc(utcTime).unix()
	}

	if (!timestamp) return null // Return null or a placeholder if timestamp is not set

	const correctedTimestamp = seccondsFromTimestamp(timestamp)
	// Format the timestamp
	return dayjs.unix(correctedTimestamp).format(formatedAs)
}
FormatedTimestamp.propTypes = {
	timestamp: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	formatedAs: PropTypes.string,
	utcTime: PropTypes.string
}
